import { isNil } from 'lodash';

import { CustomerAdditionalInfoResponseModel } from 'api/adminApiClient';
import { areObjectValuesPresent } from 'utils/objectUtils';

export const ADMIN_ONLY_KEYS = {
  contractRequirements: ['declinationReason'],
  financialOverview: ['customerCreditNotes', 'dunsNumber'],
  assignmentRequirements: ['cspNotes'],
} as const;

// Mirrors areObjectValuesPresent (treats boolean false as present via !isNil),
// but ignores admin-only keys so member-visible content is evaluated correctly.
export const areMemberVisibleObjectValuesPresent = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj?: { [key: string]: any },
  adminOnlyKeys: readonly string[] = [],
): boolean => {
  if (!obj) {
    return false;
  }

  return Object.entries(obj).some(
    ([key, value]) => !adminOnlyKeys.includes(key) && !isNil(value),
  );
};

// Single source of truth for whether a Member has any visible Additional
// Information. Must stay in sync with ClientAdditionalInfoSection rendering.
export const hasMemberVisibleAdditionalInfo = (
  data?: CustomerAdditionalInfoResponseModel,
): boolean => {
  if (!data) {
    return false;
  }

  const { contractInformation, financialInformation, assignmentInformation } =
    data;

  const noticeParty = contractInformation?.noticeParty;
  const isNoticePartyVisible =
    !!noticeParty?.attentionParty ||
    !!noticeParty?.email ||
    !!noticeParty?.phone ||
    !!noticeParty?.faxNumber ||
    !!noticeParty?.noticeMethod ||
    areObjectValuesPresent(noticeParty?.location);

  return (
    areMemberVisibleObjectValuesPresent(
      contractInformation?.insuranceRequirements,
    ) ||
    areMemberVisibleObjectValuesPresent(
      contractInformation?.contractRequirements,
      ADMIN_ONLY_KEYS.contractRequirements,
    ) ||
    isNoticePartyVisible ||
    areMemberVisibleObjectValuesPresent(
      financialInformation?.financialOverview,
      ADMIN_ONLY_KEYS.financialOverview,
    ) ||
    areMemberVisibleObjectValuesPresent(financialInformation?.rateOverview) ||
    areMemberVisibleObjectValuesPresent(
      financialInformation?.mspOrVmsOverview,
    ) ||
    areMemberVisibleObjectValuesPresent(
      assignmentInformation?.assignmentRequirements,
      ADMIN_ONLY_KEYS.assignmentRequirements,
    ) ||
    areMemberVisibleObjectValuesPresent(
      assignmentInformation?.specialAssignmentRequirements,
    )
  );
};
